import { getAnalysisURL } from '@modules/bookmark/helpers';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  getAnalysisTypeDisplayName,
  getDashboardLink,
  removeContentFromMetadata,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { useUpsertDashboard } from '@modules/dashboard/hooks';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { EditableDashboard } from '@modules/dashboard/types';
import { AnalysisViewFragment } from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

export const useRemoveAnalysisFromDashboard = () => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();
  const { showSuccessNotification } = useNotificationContext();
  const { upsertDashboard, loading } = useUpsertDashboard();
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const metadata = useDashboardStore(({ metadata }) => metadata);
  const dashboardTypeDisplayName = useDashboardStore(
    ({ visual }) => visual.dashboardTypeDisplayName,
  );

  const removeAnalysis = useCallback(
    async ({
      dashboard,
      analysis,
    }: {
      dashboard: EditableDashboard;
      analysis: AnalysisViewFragment;
    }) => {
      const updatedMetadata = removeContentFromMetadata({
        contentID: analysis.id,
        metadata,
      });

      await upsertDashboard({
        dashboardProps: {
          id: dashboard.id,
          viewToken: dashboard.viewToken,
          editToken: dashboard.editToken,
          removeAnalysisIDs: [analysis.id],
          metadataV2: updatedMetadata,
        },
      });

      if (updatedMetadata) {
        dispatch({
          type: DashboardActionType.SET_METADATA,
          payload: updatedMetadata,
        });
      }

      showSuccessNotification({
        contentComponent: `${getAnalysisTypeDisplayName(analysis.useCase)} "${
          analysis.title
        }" has been removed from the ${dashboardTypeDisplayName}`,
        props: {},
      });

      trackDashboardEvent({
        eventName: 'Analysis removed from Dashboard',
        dashboardLink: getDashboardLink(dashboard, tenantSlug),
        analysisLink: getAnalysisURL(analysis, tenantSlug),
        from: 'Dashboard Page',
      });
    },
    [
      metadata,
      upsertDashboard,
      showSuccessNotification,
      dashboardTypeDisplayName,
      tenantSlug,
      dispatch,
    ],
  );

  return {
    removeAnalysis,
    loading,
  };
};
